import { toArray } from "@suse-o11y/to-array";

import {
	type ComponentType,
	type Domain,
	type GroupRelation,
	type IndirectRelation,
	type Layer,
	type NodeId,
	type TypeGroup,
	type ViewComponent,
	ViewElementRetrievalSource,
	type ViewRelation,
} from "~/models/dto";

import type { TopologySnapshot } from "../app/state/topology/TopologySnapshot";
import type { AllRelationTypes } from "../stackviz/rendering/RenderInput";

/**
 * Atm ViewSnapshot contains data about components/groups/relations and etc
 * so here is an array of all components (visible and not visible)
 * each component has a flag `visible` - tells whenever it should be displayed on the screen
 * also, each group has a set of components, and if a component is a part of a group - it should NOT be displayed
 *
 * the similar logic with relations
 *
 * TODO merge with TopologySnapshot https://stackstate.atlassian.net/browse/STAC-19339
 */
export class StackViewSnapshot {
	public static fromTopologySnapshot(snapshot: TopologySnapshot): StackViewSnapshot {
		return new StackViewSnapshot(snapshot);
	}

	readonly componentsLookUpMap: ReadonlyMap<NodeId, ViewComponent>;

	readonly visibleComponents: ReadonlyArray<ViewComponent>;

	readonly groupRelations: ReadonlyArray<GroupRelation>;

	readonly groups: ReadonlyArray<TypeGroup>;

	readonly visibleComponentsNotInGroups: ReadonlyArray<ViewComponent>;

	readonly visibleRelationsNotInGroups: ReadonlyArray<ViewRelation>;
	readonly indirectRelationsNotInGroups: ReadonlyArray<IndirectRelation>;

	readonly visibleComponentsNotInGroupsAndGroups: ReadonlyMap<NodeId, ViewComponent | TypeGroup>;

	readonly neighborsNotInQueryResult: ReadonlyMap<NodeId, number>; // Count the neighbors which are part of the query result.

	readonly allRelationsNotInGroups: ReadonlyArray<AllRelationTypes>;
	readonly layers: ReadonlyArray<Layer>;
	readonly domains: ReadonlyArray<Domain>;
	readonly componentTypes: ReadonlyMap<string, ComponentType>;
	readonly groupedByLayers: boolean;
	readonly groupedByDomains: boolean;

	constructor(snapshot: TopologySnapshot) {
		this.componentsLookUpMap = snapshot.components;
		this.visibleComponents = toArray(snapshot.components.values());
		this.groups = toArray(snapshot.groups.values());
		this.groupRelations = toArray(snapshot.groupRelations.values());

		const componentIdsInGroups = new Set(toArray(snapshot.groups.values()).flatMap(({ components }) => components));
		const isRelationNotInInGroup = <TRelation extends { source: NodeId; target: NodeId }>({
			source,
			target,
		}: TRelation) => !(componentIdsInGroups.has(source) || componentIdsInGroups.has(target));

		const visibleComponentsMap = new Map(this.visibleComponents.map((component) => [component.id!, component]));
		const isRelationVisible = <TRelation extends { source: NodeId; target: NodeId }>({ source, target }: TRelation) =>
			visibleComponentsMap.has(source) && visibleComponentsMap.has(target);

		this.visibleComponentsNotInGroups = this.visibleComponents.filter(
			(component) => !componentIdsInGroups.has(component.id!),
		);

		this.visibleRelationsNotInGroups = toArray(snapshot.relations.values())
			.filter(isRelationVisible)
			.filter(isRelationNotInInGroup);

		this.indirectRelationsNotInGroups = toArray(snapshot.indirectRelations.values()).filter(isRelationNotInInGroup);

		const neighborsInQueryResult = toArray(snapshot.relations.values()).reduce<Map<NodeId, number>>((acc, relation) => {
			if (this.componentsLookUpMap.get(relation.source)?.retrievalSource === ViewElementRetrievalSource.Query) {
				acc.set(relation.target, (acc.get(relation.target) ?? 0) + 1);
			}
			if (this.componentsLookUpMap.get(relation.target)?.retrievalSource === ViewElementRetrievalSource.Query) {
				acc.set(relation.source, (acc.get(relation.source) ?? 0) + 1);
			}
			return acc;
		}, new Map());

		this.neighborsNotInQueryResult = new Map(
			toArray(snapshot.components.values(), (component) => [
				component.id!,
				(component.incomingRelations?.length ?? 0) +
					(component.outgoingRelations?.length ?? 0) -
					(neighborsInQueryResult.get(component.id!) ?? 0),
			]),
		);

		// TODO possible bug, don't put groups and components in the same Map
		this.visibleComponentsNotInGroupsAndGroups = new Map(
			[...this.visibleComponentsNotInGroups, ...this.groups].map((value) => [value.id!, value]),
		);

		this.allRelationsNotInGroups = [
			...this.visibleRelationsNotInGroups,
			...this.indirectRelationsNotInGroups,
			...this.groupRelations,
		];

		this.layers = snapshot.layers;
		this.domains = snapshot.domains;
		this.componentTypes = snapshot.componentTypes;
		this.groupedByLayers = snapshot.groupedByLayers;
		this.groupedByDomains = snapshot.groupedByDomains;
	}

	/**
	 * number of elements that could be displayed on the Topology Visualiser
	 */
	public numberOfVisibleElements(): number {
		return this.groups.length + this.visibleComponentsNotInGroups.length + this.allRelationsNotInGroups.length;
	}
}
